import { useCallback } from "react";
import useSWRImmutable from "swr/immutable";
import useSWRMutation from "swr/mutation";
import { Paginated, PaginationQuery } from "../../types";
import { buildUrl, mutateCache, useApi } from "../api";
import { CustomError } from "../errors";
import { AdminBorderUprightMapping, UpsertBorderUprightMappingBody } from "./types/uprightMappings";

export const useUprightMappingsApi = () => {
  const api = useApi();

  const getAdminUprightMapping = useCallback(
    (id: string, signal: AbortSignal) => {
      const url = `/admin/settings/border/upright_mappings/${id}`;
      return useSWRMutation<AdminBorderUprightMapping, CustomError, string>(url, (url) =>
        api.get<AdminBorderUprightMapping>({ url, signal }),
      );
    },
    [api],
  );

  const useAdminUprightMappings = useCallback(
    (systemId: string, query: PaginationQuery, signal: AbortSignal) => {
      const url = buildUrl(`/admin/settings/border/systems/${systemId}/upright_mappings`, query);
      return useSWRImmutable<Paginated<AdminBorderUprightMapping>, CustomError, string>(url, (url) =>
        api.get<Paginated<AdminBorderUprightMapping>>({ url, signal }),
      );
    },
    [api],
  );

  const insertAdminUprightMapping = useCallback(
    async (systemId: string, body: UpsertBorderUprightMappingBody): Promise<{ id: string }> => {
      const url = "/admin/settings/border/upright_mappings";
      const id = await api.post<{ id: string }, UpsertBorderUprightMappingBody>(url, body);
      mutateCache([url, `/admin/settings/border/systems/${systemId}/upright_mappings`]);
      return id;
    },
    [api],
  );

  const updateAdminUprightMapping = useCallback(
    async (systemId: string, uprightMappingId: string, body: UpsertBorderUprightMappingBody): Promise<void> => {
      const url = `/admin/settings/border/upright_mappings/${uprightMappingId}`;
      await api.put<UpsertBorderUprightMappingBody>(url, body);
      mutateCache([
        "/admin/settings/border/upright_mappings",
        `/admin/settings/border/systems/${systemId}/upright_mappings`,
      ]);
    },
    [api],
  );

  const deleteAdminUprightMapping = useCallback(
    async (systemId: string, uprightMappingId: string): Promise<void> => {
      const url = `/admin/settings/border/upright_mappings/${uprightMappingId}`;
      await api.del(url);
      mutateCache([
        "/admin/settings/border/upright_mappings",
        `/admin/settings/border/systems/${systemId}/upright_mappings`,
      ]);
    },
    [api],
  );

  return {
    getAdminUprightMapping,
    useAdminUprightMappings,
    insertAdminUprightMapping,
    updateAdminUprightMapping,
    deleteAdminUprightMapping,
  };
};
